import { useState } from 'react';
import styled from 'styled-components';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import IconButton from './shared/IconButton';
import Modal from './Modal';
import Button from './Button';

const PhotoGrid = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  overflow-x: auto;
  padding-bottom: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const PhotoContainer = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 200px;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 120px;
    height: 90px;
  }

  &:hover {
    .delete-button {
      opacity: 1;
    }
  }
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DeleteButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing.xs};
  right: ${({ theme }) => theme.spacing.xs};
  opacity: 0;
  transition: opacity 0.2s;
  background: rgba(0, 0, 0, 0.5);
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  svg {
    color: white;
  }
`;

const ViewAllButton = styled(Button)`
  flex: 0 0 auto;
  height: 150px;
  width: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => `${theme.colors.secondary}10`};

  @media (max-width: 768px) {
    height: 90px;
    width: 120px;
  }
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const FullScreenModal = styled(Modal)`
  .modal-container {
    padding: 0;
    max-width: 100vw;
    max-height: 100vh;
    background: transparent;
  }
`;

const FullScreenImage = styled.img`
  max-width: 100vw;
  max-height: 100vh;
  object-fit: contain;
`;

const CloseButton = styled(IconButton)`
  position: fixed;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;

  svg {
    color: white;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const PhotoGallery = ({ photos, onDelete }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fullScreenPhoto, setFullScreenPhoto] = useState(null);
  const displayPhotos = photos.slice(0, 3);
  const hasMorePhotos = photos.length > 3;

  const handlePhotoClick = (photoUrl, e) => {
    e.stopPropagation();
    setFullScreenPhoto(photoUrl);
  };

  return (
    <>
      <PhotoGrid>
        {displayPhotos.map((photoUrl, index) => (
          <PhotoContainer key={index}>
            <Photo 
              src={photoUrl} 
              alt={`Room photo ${index + 1}`} 
              onClick={(e) => handlePhotoClick(photoUrl, e)}
              style={{ cursor: 'pointer' }}
            />
            <DeleteButton
              className="delete-button"
              variant="danger"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(photoUrl);
              }}
            >
              <TrashIcon />
            </DeleteButton>
          </PhotoContainer>
        ))}
        {hasMorePhotos && (
          <ViewAllButton onClick={() => setIsModalOpen(true)}>
            View All ({photos.length})
          </ViewAllButton>
        )}
      </PhotoGrid>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <h2>All Photos</h2>
        <ModalGrid>
          {photos.map((photoUrl, index) => (
            <PhotoContainer key={index}>
              <Photo 
                src={photoUrl} 
                alt={`Room photo ${index + 1}`} 
                onClick={(e) => handlePhotoClick(photoUrl, e)}
                style={{ cursor: 'pointer' }}
              />
              <DeleteButton
                className="delete-button"
                variant="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(photoUrl);
                }}
              >
                <TrashIcon />
              </DeleteButton>
            </PhotoContainer>
          ))}
        </ModalGrid>
      </Modal>

      <FullScreenModal 
        isOpen={!!fullScreenPhoto} 
        onClose={() => setFullScreenPhoto(null)}
        className="fullscreen-modal"
      >
        <CloseButton onClick={() => setFullScreenPhoto(null)}>
          <XMarkIcon />
        </CloseButton>
        <FullScreenImage src={fullScreenPhoto} alt="Full screen photo" />
      </FullScreenModal>
    </>
  );
};

export default PhotoGallery; 